import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet'; // Essential for dynamic meta tags
import { blogPosts } from '../data/blog';
import { FiArrowLeft, FiCalendar, FiTag, FiShare2 } from 'react-icons/fi';

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Find the post based on the ID
  const post = blogPosts.find((p) => p.id === parseInt(id));

  if (!post) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold text-slate-800">Insight Report Not Found</h1>
        <button onClick={() => navigate('/blog')} className="mt-4 text-[#0054A6] font-bold underline">
          Return to Industry Insights
        </button>
      </div>
    );
  }

  // Structured Data for Google (Schema.org)
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://protechconstruction.com" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://protechconstruction.com/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title }
    ]
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* DYNAMIC SEO META TAGS */}
      <Helmet>
        <title>{`${post.title} | protech Construction Insights`}</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={`${post.category}, Petroleum Construction, Civil Engineering, HPCL Maintenance, ${post.title}`} />
        
        {/* Open Graph (Facebook/LinkedIn) */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:section" content={post.category} />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:image" content={post.image} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="p-6 border-b bg-slate-50/50 sticky top-0 z-10 backdrop-blur-md">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-[#0054A6] font-bold hover:text-[#EE3124] transition-all group"
          >
            <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" /> 
            Back to Insights
          </button>
          
          <button 
            onClick={() => navigator.share?.({ title: post.title, url: window.location.href })}
            className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500"
            title="Share this report"
          >
            <FiShare2 />
          </button>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 mt-12">
        {/* Header Metadata */}
        <header className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <span className="flex items-center gap-1 text-xs font-black text-[#EE3124] uppercase tracking-widest bg-red-50 px-3 py-1 rounded-full">
              <FiTag /> {post.category}
            </span>
            <time 
              dateTime={post.date}
              className="flex items-center gap-1 text-xs font-bold text-slate-400 uppercase tracking-widest"
            >
              <FiCalendar /> {post.date}
            </time>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.1] mb-8 tracking-tight">
            {post.title}
          </h1>
        </header>

        {/* Featured Image - Optimized with alt text */}
        <figure className="rounded-3xl overflow-hidden mb-12 shadow-2xl ring-1 ring-slate-200">
          <img 
            src={post.image} 
            alt={`Technical analysis of ${post.title} in the Petroleum sector`} 
            className="w-full h-auto object-cover max-h-[600px]"
            loading="eager" // Load first image immediately
          />
          <figcaption className="p-4 text-xs italic text-slate-400 text-center bg-slate-50">
            Visual overview of {post.title} implementation.
          </figcaption>
        </figure>

        {/* Content Body */}
        <div className="prose prose-slate prose-lg max-w-none">
          {/* Highlighted Lead Paragraph */}
          <p className="text-xl md:text-2xl font-medium text-slate-600 mb-10 leading-relaxed border-l-4 border-[#0054A6] pl-6 italic">
            {post.excerpt}
          </p>

          <div className="text-slate-800 text-lg leading-loose space-y-8">
            {post.description.split('\n').map((para, index) => (
              para.trim() && <p key={index} className="first-letter:text-3xl first-letter:font-bold">{para}</p>
            ))}
          </div>
        </div>

        {/* Footer Navigation - Good for keeping users on site (SEO Metric: Dwell Time) */}
        <footer className="mt-20 pt-10 border-t border-slate-100">
          <div className="bg-slate-50 p-8 rounded-[2rem] flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h4 className="font-black text-slate-900 uppercase text-sm tracking-widest">protech Construction</h4>
              <p className="text-slate-500 text-sm">Specialists in Petroleum & Industrial Infrastructure.</p>
            </div>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-[#0054A6] text-white px-8 py-3 rounded-full font-bold hover:bg-[#EE3124] transition-colors shadow-lg"
            >
              Consult with Experts
            </button>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default BlogDetails;